import { useMemo, useRef, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

/**
 * WYSIWYG email body editor — a thin wrapper around Quill (via
 * ``react-quill-new`` for React 19 compat) that outputs raw HTML
 * ready for the backend's ``{{token}}`` interpolator.
 *
 * Design notes:
 * - Toolbar covers everything admins reach for in email templates
 *   (headings, bold/italic/underline, colour, lists, blockquote,
 *   link, alignment, clean). Anything more esoteric can still be
 *   authored in HTML via the "source" toggle button we surface at the
 *   footer.
 * - ``tokens`` prop renders a small "Insert token" dropdown above the
 *   editor — clicking a token pastes ``{{token}}`` at the cursor so
 *   admins never have to hand-type interpolators.
 * - Dark-theme skin is applied via targeted CSS overrides in
 *   ``.rich-email-editor`` (see /app/frontend/src/rich-email-editor.css
 *   imported globally from index.js).
 */
export default function RichEmailEditor({
  value, onChange, tokens = [], placeholder, testid,
}) {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["blockquote", "link"],
        ["clean"],
      ],
    },
    clipboard: { matchVisual: false },
  }), []);

  const formats = useMemo(() => [
    "header", "bold", "italic", "underline",
    "color", "background",
    "list", "align", "blockquote", "link",
  ], []);

  function insertToken(token) {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const sel = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    editor.insertText(sel.index, `{{${token}}}`, "user");
    editor.setSelection(sel.index + token.length + 4, 0);
  }

  // Focus the editor when the placeholder placeholder attribute lands late
  // (Quill sometimes ignores the initial placeholder if the ref isn't ready).
  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (editor && placeholder && !editor.root.getAttribute("data-placeholder")) {
      editor.root.setAttribute("data-placeholder", placeholder);
    }
  }, [placeholder]);

  return (
    <div className="rich-email-editor" data-testid={testid}>
      {tokens.length > 0 && (
        <div className="rich-email-editor__tokens">
          <span className="rich-email-editor__tokens-label">Insert token</span>
          <div className="rich-email-editor__tokens-list">
            {tokens.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => insertToken(t)}
                data-testid={testid ? `${testid}-token-${t}` : undefined}
                className="rich-email-editor__token-btn"
                title={`Insert {{${t}}} at cursor`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
}
